import { DateTime } from 'luxon';

const loadSvgSprite = (spriteUrl) => {
    fetch(spriteUrl, {
        method: 'GET',
    })
        .then((response) => response.text())
        .then((data) => {
            const div = document.createElement('div');

            div.innerHTML = data;
            div.classList.add('svg-sprite');
            document.body.insertBefore(div, document.body.childNodes[0]);
        });
};

const objectToQueryString = (object) => {
    const params = [];

    for (const property in object) {
        if (Object.prototype.hasOwnProperty.call(object, property)) {
            params.push(`${encodeURIComponent(property)}=${encodeURIComponent(object[property])}`);
        }
    }

    return params.join('&');
};

const getCookie = (name) => {
    const v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
    return v ? v[2] : null;
};

const setCookie = (name = '', value = '', maxAge) => {
    let cookie;

    if (name.length && value.length) {
        cookie = `${name}=${encodeURIComponent(value)};path=/`;

        if (maxAge) {
            cookie = `${cookie};max-age=${maxAge}`;
        }
    }

    if (cookie && cookie.length) {
        document.cookie = cookie;
    }
};

const getOrdinal = (day) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = day % 100;

    return day + (s[(v - 20) % 10] || s[v] || s[0]);
};

const getUnixTimestamp = () => {
    return Math.floor(Date.now() / 1000);
};

const getRelativePublishDate = (unixTimestamp) => {
    if (!unixTimestamp) {
        return;
    }

    const dateCreated = DateTime.fromSeconds(unixTimestamp);
    const isToday = DateTime.now().hasSame(dateCreated, 'days');
    const isYesterday = DateTime.now().minus({ days: 1 }).hasSame(dateCreated, 'days');
    let datePosted = dateCreated.toRelative();

    if (isToday) {
        datePosted = dateCreated.toRelativeCalendar();
    } else if (isYesterday) {
        datePosted = DateTime.now().minus({ days: 1 }).toRelative();
    }

    if (datePosted.match(/^a\s{1}/)) {
        datePosted = `1 ${datePosted.split('a ')[1]}`;
    }

    return datePosted;
};

const getPublishDateTimestamp = (unixTimestamp) => {
    if (!unixTimestamp) {
        return;
    }

    return DateTime.fromSeconds(unixTimestamp).toISODate();
};

const getPublishDate = (unixTimestamp) => {
    if (!unixTimestamp) {
        return;
    }

    const date = DateTime.fromSeconds(unixTimestamp);
    const ordinalDay = getOrdinal(date.toFormat('d'));

    return `${ordinalDay} ${date.toFormat('LLLL yyyy')}`;
};

const getNamespace = (element) => {
    if (!element) {
        return;
    }

    return element.dataset.namespace ?? window.location.pathname.split('/')?.[1];
};

const dec2hex = (decimal) => {
    return ('0' + decimal.toString(16)).substr(-2);
};

const generateUid = (length = 40) => {
    const seed = new Uint8Array(length / 2);
    const crypto = window.crypto || window.msCrypto;

    crypto.getRandomValues(seed);

    return Array.from(seed, dec2hex).join('');
};

const secondsToTime = (seconds, substrFormat = [11, 8]) => {
    return new Date(seconds * 1000).toISOString().substr(substrFormat[0], substrFormat[1]);
};

const replaceElementContents = (parentEl, childEl, htmlString) => {
    const htmlEl = document.createRange().createContextualFragment(htmlString);

    if (childEl) {
        childEl.replaceWith(htmlEl);
    } else {
        parentEl.appendChild(htmlEl);
    }
};

const getElementDimensions = (element) => {
    const elStyle = getComputedStyle(element);
    const width = parseInt(elStyle.width.replace('px', ''), 10);
    const height = parseInt(elStyle.height.replace('px', ''), 10);
    const paddingWidth =
        parseInt(elStyle.paddingLeft.replace('px', ''), 10) + parseInt(elStyle.paddingRight.replace('px', ''), 10);
    const paddingHeight =
        parseInt(elStyle.paddingTop.replace('px', ''), 10) + parseInt(elStyle.paddingBottom.replace('px', ''), 10);

    return {
        width: width,
        height: height,
        innerWidth: width - paddingWidth,
        innerHeight: height - paddingHeight,
        paddingWidth: paddingWidth,
        paddingHeight: paddingHeight,
    };
};

const formatRatingValue = (value) => {
    return (value / 10).toFixed(1);
};

const getSearchStateUrl = (contentType, state, baseUrl = PULSE.search.url) => {
    const urlParts = [`${baseUrl}?`];
    const indexConfig = PULSE.search.config?.[contentType];
    let indexName = '';

    // Get the default index name for this content type
    if (indexConfig) {
        indexName = indexConfig.defaultIndex;
    } else {
        throw new Error(`No search index defined for content type '${contentType}'`);
    }

    // Build URL from state
    for (const [stateKey, stateValue] of Object.entries(state)) {
        for (const [itemKey, itemValue] of Object.entries(stateValue)) {
            if (Array.isArray(itemValue)) {
                itemValue.forEach((value, index) => {
                    if (value) {
                        const partKey = encodeURIComponent(`[${stateKey}][${itemKey}][${index}]`);
                        const partValue = encodeURIComponent(value.replace(/<[^>]*>/g, '').replaceAll('&amp;', '&'));

                        urlParts.push(`${indexName}${partKey}=${partValue}&`);
                    }
                });
            } else {
                if (itemValue) {
                    const partKey = encodeURIComponent(`[${stateKey}][${itemKey}]`);
                    const partValue = encodeURIComponent(itemValue.replace(/<[^>]*>/g, '').replaceAll('&amp;', '&'));

                    urlParts.push(`${indexName}${partKey}=${partValue}&`);
                }
            }
        }
    }

    // Create initial URL string and remove trailing ampersand
    let urlString = urlParts.join('').replace(/&$/, '');

    // Add in tab ID
    urlString = `${urlString}#tab-${indexConfig.tabId}`;

    return urlString;
};

const addAlgoliaHighlights = (resultItem) => {
    const hl = resultItem?._highlightResult;
    if (hl) {
        for (const [key, value] of Object.entries(hl)) {
            // 1) Arrays
            if (Array.isArray(value)) {
                if (!Array.isArray(resultItem[key])) {
                    continue;
                }

                value.forEach((hlItem, index) => {
                    const target = resultItem[key][index];

                    // Case A: array of primitives (strings/numbers)
                    // highlight item shape: { value, matchLevel, matchedWords }
                    if (typeof target === 'string' || typeof target === 'number' || target == null) {
                        if (hlItem && typeof hlItem === 'object' && 'value' in hlItem) {
                            resultItem[key][index] = hlItem.value ?? String(target ?? '');
                        }
                        return;
                    }

                    // Case B: array of objects
                    if (hlItem && typeof hlItem === 'object') {
                        // Special case: geographies/location title -> also highlight countryCode
                        if ((key === 'geographies' || key === 'location') && hlItem.title?.matchLevel === 'full') {
                            if (hlItem.countryCode) {
                                hlItem.countryCode.matchLevel = 'full';
                                hlItem.countryCode.value = `<em>${hlItem.countryCode.value}</em>`;
                            }
                        }

                        for (const [itemKey, itemValue] of Object.entries(hlItem)) {
                            // Algolia highlight leaf: { value, matchLevel, ... }
                            if (itemValue && typeof itemValue === 'object' && 'value' in itemValue) {
                                target[itemKey] = itemValue.value ?? '';
                                target.matched = target.matched || itemValue.matchLevel !== 'none';
                            }
                        }
                    }
                });
            } else {
                // 2) Non-array highlight results (strings/objects)
                // Common: title: { value, matchLevel }
                if (value && typeof value === 'object' && 'value' in value) {
                    resultItem[key] = value.value;
                }
            }
        }
    }

    // Snippets (keep mostly same, but guard primitives)
    const sn = resultItem?._snippetResult;
    if (sn) {
        for (const [key, value] of Object.entries(sn)) {
            if (Array.isArray(value)) {
                if (!Array.isArray(resultItem[key])) continue;

                value.forEach((snItem, index) => {
                    const target = resultItem[key][index];
                    if (target == null || typeof target !== 'object') return;

                    for (const [itemKey, itemValue] of Object.entries(snItem)) {
                        if (itemValue?.matchLevel !== 'none') {
                            target[itemKey] = itemValue.value;
                        }
                    }
                });
            } else {
                if (value?.matchLevel !== 'none') {
                    resultItem[key] = value.value;
                }
            }
        }
    }
};

function getLabelWithCardinality(label, total) {
    return total > 1 ? `${label}s` : label;
}

const isItemTrending = (id) => {
    return PULSE.trending.items.includes(id);
};

const getTrendingBadge = () => {
    return PULSE.trending.badge;
};

// HTML-escapes a string so it can be safely inserted into innerHTML without letting the browser interpret it as markup or script.
const escapeHtml = (str) =>
    String(str).replace(
        /[&<>"']/g,
        (c) =>
            ({
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;', // eslint
            })[c]
    );

const decodeHtmlEntities = (html) => {
    const el = document.createElement('textarea');
    el.innerHTML = String(html ?? '');
    return el.value;
};

const humanizeIdentifier = (s) =>
    String(s)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .toLowerCase();

const stripTagsButKeepMark = (inputHtml) => {
    const tpl = document.createElement('template');
    tpl.innerHTML = String(inputHtml ?? '');

    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent);
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const el = node;
        if (el.tagName === 'MARK') return `<mark>${escapeHtml(el.textContent)}</mark>`;

        return Array.from(el.childNodes).map(walk).join('');
    };

    return Array.from(tpl.content.childNodes).map(walk).join('');
};

const getIconSvgName = (type) => {
    switch (type) {
        case 'product':
            return 'products';
        case 'journeyType':
            return 'milestone';
        case 'businessType':
            return 'finance';
        case 'brand':
            return 'enterprise';
        case 'tags':
        case 'combinedTags':
        case 'fcTags':
            return 'tag';
        case 'uiTags':
            return 'gui';
        default:
            return '';
    }
};

const getMedia = (media) => {
    switch (media) {
        case 'video':
            return 'videos';
        case 'screenshot':
            return 'screenshots';
        case 'article':
            return 'newsInsights';
        default:
            return 'videos';
    }
};

export const getDisplayType = (type) => {
    switch (type) {
        case 'uiTags':
        case 'uiTag':
            return 'UI Element';
        case 'fcTags':
        case 'fcTag':
            return 'screenshot tag';
        case 'combinedTags':
            return 'screenshot tag';
        case 'tags':
        case 'tag':
            return 'video tag';
        case 'brand':
            return 'brand';
        case 'journeyType':
            return 'journey type';
        case 'businessType':
            return 'business type';
        case 'product':
            return 'product';
        default:
            return type;
    }
};

const getTagTooltipData = (tag, type, media, attribute, taxonomyBaseUrl) => {
    const hasMarks = /<mark\b/i.test(tag);

    const rawTag = hasMarks
        ? decodeHtmlEntities(new DOMParser().parseFromString(tag, 'text/html').body.textContent || '')
        : decodeHtmlEntities(tag);

    const refinementList = { [attribute]: [tag] };

    // This is brittle but works for now. TODO: refactor and use the same display types everywhere (card.js, screenshot card, article card etc).
    const taxonomyLink = getSearchStateUrl(getMedia(media), { refinementList }, taxonomyBaseUrl ?? taxonomyBaseUrl);
    const displayType = getDisplayType(type);
    const trimmedTag = rawTag.trim();
    const labelHtml = hasMarks ? stripTagsButKeepMark(tag) : escapeHtml(rawTag);
    const screenshotTagOnVideoCardText = type === 'tag' && media === 'video' ? 'a screenshot in' : '';

    const tooltipText = `
        The term &quot;${escapeHtml(trimmedTag)}&quot; is a ${escapeHtml(displayType)} associated with ${screenshotTagOnVideoCardText} this ${escapeHtml(media)}`;

    return {
        icon: getIconSvgName(type),
        label: labelHtml,
        showLabel: true,
        cssClasses: hasMarks ? ['badge', 'badge--mark'] : ['tag', 'badge'],
        text: tooltipText,
        url: taxonomyLink,
        taxonomyUrl: taxonomyLink,
    };
};

const getTitleTooltipData = (title, url) => {
    const hasMarks = /<mark\b/i.test(title);

    const rawTitle = hasMarks
        ? decodeHtmlEntities(new DOMParser().parseFromString(title, 'text/html').body.textContent || '')
        : decodeHtmlEntities(title);

    const trimmedTag = title.trim();
    const labelHtml = hasMarks ? stripTagsButKeepMark(rawTitle) : escapeHtml(trimmedTag);

    return {
        label: hasMarks ? trimmedTag : labelHtml,
        showLabel: true,
        cssClasses: [''],
        text: labelHtml,
        url: url,
    };
};

const renderOCRHighlights = (matchedWords) => {
    if (!matchedWords || !matchedWords.length) {
        return '';
    }

    const highlights = matchedWords
        .map((word, index) => {
            const [x1, y1, x2, y2] = word.box;

            // Coordinates are normalized (0-1), convert directly to percentages
            const left = x1 * 100;
            const top = y1 * 100;
            const width = (x2 - x1) * 100;
            const height = (y2 - y1) * 100;

            // Strip <mark> tags from text
            const cleanText = word.text.replace(/<\/?mark>/g, '');

            return `
            <div class="ocr-highlight" 
                 style="
                    left: ${left}%;
                    top: ${top}%;
                    width: ${width}%;
                    height: ${height}%;
                 " 
                 data-ocr-text="${cleanText}"
                 data-ocr-index="${index}">
            </div>
        `;
        })
        .join('');

    return `<div class="ocr-highlights-overlay">${highlights}</div>`;
};

const buildSearchRefinementUrl = ({ baseUrl, value, attr, attrValues, indexName, tab, extraRefinements = {} }) => {
    const params = new URLSearchParams();

    if (value !== '') {
        params.set(`${indexName}[query]`, value);
    }

    const addRefinementValues = (refinementAttr, values = []) => {
        values.forEach((refinementValue, index) => {
            params.set(`${indexName}[refinementList][${refinementAttr}][${index}]`, refinementValue);
        });
    };

    // Use the full array for the primary attribute, falling back to [value] for safety
    addRefinementValues(attr, attrValues?.length ? attrValues : [value]);

    Object.entries(extraRefinements).forEach(([refinementAttr, refinementValues]) => {
        if (refinementAttr === attr) return;
        addRefinementValues(refinementAttr, refinementValues);
    });

    const queryString = params.toString();
    return `${baseUrl}?${queryString}#tab-${tab}`;
};

let _translationMouseOverHandler = null;
let _translationMouseMoveHandler = null;
let _translationDblClickHandler = null;

const initScreenshotTranslation = (slideSelector = '.screenshot-modal__slide', language = '') => {
    const enabledCheckbox = document.querySelector('#enableTranslations');

    // Create/find the tooltip BEFORE the guard, so the early-return path can touch it safely.
    let translateTooltip = document.querySelector('.gemini-tooltip');
    if (!translateTooltip) {
        translateTooltip = document.createElement('div');
        translateTooltip.className = 'gemini-tooltip';
        document.body.appendChild(translateTooltip);
    }

    // if enabled translations is false, ensure tooltip is hidden and return early without attaching any event handlers
    if (!enabledCheckbox?.checked) {
        translateTooltip.style.opacity = '0';
        return;
    }

    const showTooltipAt = (x, y) => {
        translateTooltip.style.opacity = '1';
        translateTooltip.style.left = `${x + 10}px`;
        translateTooltip.style.top = `${y + 10}px`;
    };

    const hideTooltip = () => {
        translateTooltip.style.opacity = '0';
        document.removeEventListener('mousemove', _translationMouseMoveHandler);
    };

    // Position only. Attached lazily, runs only while the tooltip is visible.
    _translationMouseMoveHandler = (e) => {
        if (!enabledCheckbox.checked) {
            hideTooltip();
            return;
        }
        showTooltipAt(e.clientX, e.clientY);
    };

    // Detection. Fires on boundary crossings, not every pixel of movement.
    if (_translationMouseOverHandler) {
        document.removeEventListener('mouseover', _translationMouseOverHandler);
    }

    if (_translationMouseMoveHandler) {
        document.removeEventListener('mousemove', _translationMouseMoveHandler);
    }

    _translationMouseOverHandler = (e) => {
        if (!enabledCheckbox.checked) {
            hideTooltip();
            return;
        }

        const isOverOverlay = e.target.classList.contains('gemini-full-overlay');
        const slide = e.target.closest(slideSelector);
        const isActiveSlide = slide?.classList.contains('swiper-slide-active');
        const isOverImg = e.target.tagName === 'IMG' && isActiveSlide;

        if (isOverImg || isOverOverlay) {
            translateTooltip.innerText = isOverOverlay ? 'Click to hide' : `Double-click to translate to ${language}`;
            showTooltipAt(e.clientX, e.clientY);
            // Re-adding the same handler reference is a no-op, so this is safe to call repeatedly.
            document.addEventListener('mousemove', _translationMouseMoveHandler);
        } else {
            hideTooltip();
        }
    };

    document.addEventListener('mouseover', _translationMouseOverHandler);

    // Double-click translation. Guard added last turn; the geometry/render body is unchanged.
    if (_translationDblClickHandler) {
        document.removeEventListener('dblclick', _translationDblClickHandler, true);
    }
    _translationDblClickHandler = async (e) => {
        if (!enabledCheckbox.checked) return;
        if (e.target.tagName !== 'IMG') return;
        if (!e.target.closest(slideSelector)) return;

        const img = e.target;
        const rect = img.getBoundingClientRect();
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;

        const scaleX = rect.width / naturalWidth;
        const scaleY = rect.height / naturalHeight;

        const computedStyle = window.getComputedStyle(img);
        const objectFit = computedStyle.objectFit || 'fill';

        let visibleWidth = rect.width;
        let visibleHeight = rect.height;
        let offsetX = 0;
        let offsetY = 0;

        if (objectFit === 'contain' || objectFit === 'cover') {
            const imgAspect = naturalWidth / naturalHeight;
            const boxAspect = rect.width / rect.height;

            if (objectFit === 'contain') {
                if (imgAspect > boxAspect) {
                    visibleWidth = rect.width;
                    visibleHeight = rect.width / imgAspect;
                    offsetY = (rect.height - visibleHeight) / 2;
                } else {
                    visibleHeight = rect.height;
                    visibleWidth = rect.height * imgAspect;
                    offsetX = (rect.width - visibleWidth) / 2;
                }
            } else if (objectFit === 'cover') {
                if (imgAspect > boxAspect) {
                    visibleHeight = rect.height;
                    visibleWidth = rect.height * imgAspect;
                    offsetX = -(visibleWidth - rect.width) / 2;
                } else {
                    visibleWidth = rect.width;
                    visibleHeight = rect.width / imgAspect;
                    offsetY = -(visibleHeight - rect.height) / 2;
                }
            }
        }

        let masterOverlay = img._geminiOverlay;

        if (!masterOverlay) {
            masterOverlay = document.createElement('div');
            masterOverlay.className = 'gemini-full-overlay';
            document.body.appendChild(masterOverlay);
            img._geminiOverlay = masterOverlay;

            const updatePosition = () => {
                const newRect = img.getBoundingClientRect();
                masterOverlay.style.top = `${newRect.top + window.scrollY}px`;
                masterOverlay.style.left = `${newRect.left + window.scrollX}px`;
                masterOverlay.style.width = `${newRect.width}px`;
                masterOverlay.style.height = `${newRect.height}px`;
            };

            window.addEventListener('scroll', updatePosition, { passive: true });
            window.addEventListener('resize', updatePosition, { passive: true });
        }

        masterOverlay.style.display = 'block';
        masterOverlay.style.top = `${rect.top + window.scrollY}px`;
        masterOverlay.style.left = `${rect.left + window.scrollX}px`;
        masterOverlay.style.width = `${rect.width}px`;
        masterOverlay.style.height = `${rect.height}px`;
        masterOverlay.innerHTML = '<div class="gemini-scan-line"></div>';
        masterOverlay.onclick = () => {
            masterOverlay.style.display = 'none';
        };

        try {
            const translationImageUrl = img.dataset.translationSrc || img.currentSrc || img.src;
            const url = `/actions/content-module/screenshots/translate-screenshot?imageUrl=${encodeURIComponent(translationImageUrl)}`;
            const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const results = await res.json();

            if (!results || results.error) throw new Error(results?.error || 'AI Response Failed');

            masterOverlay.innerHTML = '';

            results.forEach((item) => {
                const [ymin, xmin, ymax, xmax] = item.box_2d;

                const boxWidthNatural = ((xmax - xmin) / 1000) * naturalWidth;
                const boxHeightNatural = ((ymax - ymin) / 1000) * naturalHeight;
                const leftPosNatural = (xmin / 1000) * naturalWidth;
                const topPosNatural = (ymin / 1000) * naturalHeight;

                let boxWidth;
                let boxHeight;
                let leftPos;
                let topPos;

                if (objectFit === 'fill' || objectFit === 'scale-down') {
                    boxWidth = boxWidthNatural * scaleX;
                    boxHeight = boxHeightNatural * scaleY;
                    leftPos = leftPosNatural * scaleX;
                    topPos = topPosNatural * scaleY;
                } else {
                    const visibleScaleX = visibleWidth / naturalWidth;
                    const visibleScaleY = visibleHeight / naturalHeight;
                    boxWidth = boxWidthNatural * visibleScaleX;
                    boxHeight = boxHeightNatural * visibleScaleY;
                    leftPos = offsetX + leftPosNatural * visibleScaleX;
                    topPos = offsetY + topPosNatural * visibleScaleY;
                }

                const textBlock = document.createElement('div');
                textBlock.className = 'gemini-text-block';
                textBlock.style.width = `${boxWidth}px`;
                textBlock.style.height = `${boxHeight}px`;
                textBlock.style.transform = `translate(${leftPos}px, ${topPos}px)`;
                textBlock.innerText = item.translated_text;
                textBlock.style.pointerEvents = 'auto';
                textBlock.dataset.baseTransform = `translate(${leftPos}px, ${topPos}px)`;

                textBlock.addEventListener('mouseenter', (ev) => {
                    ev.stopPropagation();
                    textBlock.style.transform = `${textBlock.dataset.baseTransform} scale(1.5)`;
                    textBlock.style.zIndex = '1000';
                    textBlock.classList.add('gemini-text-hover');
                });

                textBlock.addEventListener('mouseleave', (ev) => {
                    ev.stopPropagation();
                    textBlock.style.transform = textBlock.dataset.baseTransform;
                    textBlock.style.zIndex = '1';
                    textBlock.classList.remove('gemini-text-hover');
                });

                textBlock.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                });

                masterOverlay.appendChild(textBlock);
            });
        } catch (err) {
            masterOverlay.innerHTML = '<div class="gemini-error">Connection Error. Refresh page.</div>';
        }
    };

    document.addEventListener('dblclick', _translationDblClickHandler, true);
};

const hideAllScreenshotTranslatorOverlays = () => {
    document.querySelectorAll('.gemini-full-overlay').forEach((overlay) => {
        overlay.style.display = 'none';
    });
};

export {
    loadSvgSprite,
    objectToQueryString,
    getRelativePublishDate,
    getPublishDateTimestamp,
    getPublishDate,
    getNamespace,
    getCookie,
    setCookie,
    generateUid,
    secondsToTime,
    getUnixTimestamp,
    replaceElementContents,
    getElementDimensions,
    formatRatingValue,
    getSearchStateUrl,
    addAlgoliaHighlights,
    isItemTrending,
    getTrendingBadge,
    getOrdinal,
    getLabelWithCardinality,
    escapeHtml,
    decodeHtmlEntities,
    humanizeIdentifier,
    getTagTooltipData,
    getTitleTooltipData,
    renderOCRHighlights,
    buildSearchRefinementUrl,
    initScreenshotTranslation,
    hideAllScreenshotTranslatorOverlays,
};
